import { TriangleAlert } from "lucide-react";
import type { LineItemUnit } from "@/apis/compare/types";
import { cn } from "@/lib/utils";
import {
  type MixedScaleForeignCurrency,
  getForeignCurrencyUnitLabels,
  getMixedScaleForeignCurrencies,
  getMixedScaleWarningText,
  getSharesUnitLabelFromExponent,
  getUnitLabelFromExponent,
} from "@/utils/ixd-editor/unit-conversion";

interface TableUnitCaptionProps {
  /** 원화 단위 라벨. null 이면 원화 단위 선언이 없다 — "원"(원 단위 선언)과 다른 뜻이다. */
  unitText?: string | null;
  /** IXD 는 라인아이템 단위 맵, DSD 워크북은 표 단위 선언 배열. 표기는 여기 한 곳이 만든다. */
  foreignUnits?: Record<string, LineItemUnit> | LineItemUnit[] | null;
  sharesUnitText?: string | null;
  /**
   * 배율 혼재 경고에 hover(키보드 포커스 포함)하는 동안 true, 벗어나면 false 로 알린다.
   * 표에서 해당 행을 표시하는 일은 표를 그리는 화면이 맡는다(캡션은 표 DOM 을 모른다).
   */
  onMixedScaleWarningHover?: (hovered: boolean) => void;
  /** 캡션 정렬·여백은 화면마다 달라 호출부가 소유한다. 그릴 게 없으면 이 클래스도 함께 사라진다. */
  className?: string;
}

/**
 * 표 단위 캡션 — 원화·외화·주를 축을 나눠 보여준다.
 *
 * 외화 행은 셀 값이 표 원화 단위가 아니라 라인아이템 단위로 환산돼 표시된다
 * (table-body-cell.tsx effectiveUnitDivisor). 캡션에 원화 단위만 있으면 사용자는 그 행도
 * 원화 단위로 읽는다. 대사·워크북 매칭·미리보기가 같은 캡션을 각자 그리므로 한 컴포넌트로 모은다.
 *
 * 단위 선언이 하나도 없으면 아무것도 그리지 않는다 — "단위 없음"을 "원 단위"로 단정하지 않는다.
 *
 * 같은 통화가 다른 배율로 섞여 있으면(USD 와 USD 천) 경고를 붙인다. 같은 통화 행끼리도
 * 숫자를 나란히 비교할 수 없다는 뜻이라, 캡션 라벨 나열만으로는 사용자가 놓친다.
 * 라인아이템별 단위를 알고(IXD 맵 입력) 화면이 행 표시를 맡으면 경고에 hover 하는 동안 표에서 그 행을 표시한다.
 */
export const TableUnitCaption = ({
  unitText,
  foreignUnits,
  sharesUnitText,
  onMixedScaleWarningHover,
  className,
}: TableUnitCaptionProps) => {
  const foreignLabels = getForeignCurrencyUnitLabels(foreignUnits);
  const mixedScaleCurrencies = getMixedScaleForeignCurrencies(foreignUnits);
  if (!unitText && foreignLabels.length === 0 && !sharesUnitText) return null;

  return (
    <div
      data-ixd-dl-unit-label
      className={cn("caption-2-medium text-label-alternative", className)}
    >
      {unitText && <span>(단위: {unitText})</span>}
      {foreignLabels.length > 0 && (
        <span className={unitText ? "ml-1" : undefined}>
          (외화 단위: {foreignLabels.join(", ")})
        </span>
      )}
      {sharesUnitText && (
        <span
          className={unitText || foreignLabels.length > 0 ? "ml-1" : undefined}
        >
          (주 단위: {sharesUnitText})
        </span>
      )}
      {mixedScaleCurrencies.length > 0 && (
        <MixedScaleWarning
          currencies={mixedScaleCurrencies}
          onHover={onMixedScaleWarningHover}
        />
      )}
    </div>
  );
};

const MixedScaleWarning = ({
  currencies,
  onHover,
}: {
  currencies: MixedScaleForeignCurrency[];
  onHover?: (hovered: boolean) => void;
}) => {
  // DSD 워크북처럼 표 단위 선언만 있으면 라인아이템을 몰라 표시할 행이 없다.
  const hasLineItems = currencies.some(({ scales }) =>
    scales.some(({ lineItemIds }) => lineItemIds.length > 0),
  );
  const hoverProps =
    onHover && hasLineItems
      ? {
          // 키보드로도 같은 표시를 볼 수 있게 포커스를 받는다.
          tabIndex: 0,
          onPointerEnter: () => onHover(true),
          onPointerLeave: () => onHover(false),
          onFocus: () => onHover(true),
          onBlur: () => onHover(false),
        }
      : {};
  return (
    <span
      data-mixed-foreign-unit-warning
      className="flex items-center gap-1 text-status-caution"
      {...hoverProps}
    >
      <TriangleAlert className="size-3.5 shrink-0" />
      {getMixedScaleWarningText(currencies)}
    </span>
  );
};

/**
 * 표 미리보기 응답(배율)을 캡션 props(라벨)로 옮긴다.
 *
 * IXD 대사는 서버가 라벨(`dlView.unitText`)을 그대로 주므로 이 변환을 거치지 않는다 —
 * 배율로 접으면 "억원"처럼 `getUnitDivisor` 가 모르는 표기가 "원"으로 뭉개진다.
 */
export const toTableUnitCaptionProps = (table: {
  unit: number | null;
  foreignUnits?: LineItemUnit[];
  sharesUnit?: number | null;
}): Pick<
  TableUnitCaptionProps,
  "unitText" | "foreignUnits" | "sharesUnitText"
> => ({
  unitText: table.unit == null ? null : getUnitLabelFromExponent(table.unit),
  foreignUnits: table.foreignUnits,
  sharesUnitText:
    table.sharesUnit == null
      ? null
      : getSharesUnitLabelFromExponent(table.sharesUnit),
});
